import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, IconButton,
  TextField, Grid, Alert, Snackbar, MenuItem, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, CircularProgress, Divider,
} from '@mui/material';
import {
  CloudUpload, Delete, Search, Visibility, Science, OpenInNew, BiotechOutlined,
} from '@mui/icons-material';
import { patientService, labReportService, authService } from '../../services/api';
import TopBar from '../../components/TopBar';

const REPORT_TYPES = [
  'Blood Test', 'Urine Test', 'Stool Test',
  'X-Ray', 'Ultrasound', 'CT Scan', 'MRI', 'ECG', 'EEG',
  'Biopsy', 'Culture & Sensitivity', 'Thyroid Profile',
  'Lipid Profile', 'Liver Function Test', 'Kidney Function Test',
  'HbA1c', 'Pregnancy Test', 'Other',
];

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const LabReportsPage = () => {
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'Admin';

  // Patient search
  const [searchTerm, setSearchTerm] = useState('');
  const [allPatients, setAllPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientsLoading, setPatientsLoading] = useState(false);

  // Upload form
  const [file, setFile] = useState(null);
  const [reportType, setReportType] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Reports list
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  // My recent uploads (for lab attendant dashboard view)
  const [myReports, setMyReports] = useState([]);
  const [myReportsLoading, setMyReportsLoading] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);
  const [reportViewerOpen, setReportViewerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load all patients once for search
  useEffect(() => {
    setPatientsLoading(true);
    patientService.getAllPatients()
      .then(res => setAllPatients(res.data))
      .catch(() => {})
      .finally(() => setPatientsLoading(false));

    loadMyReports();
  }, []);

  const loadMyReports = () => {
    setMyReportsLoading(true);
    labReportService.getAll()
      .then(res => setMyReports(res.data))
      .catch(() => {})
      .finally(() => setMyReportsLoading(false));
  };

  // Filter patients by name or phone as user types
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setFilteredPatients([]);
      return;
    }
    setFilteredPatients(
      allPatients.filter(p =>
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(term) ||
        p.phoneNumber?.includes(term)
      ).slice(0, 8)
    );
  }, [searchTerm, allPatients]);

  const handleSelectPatient = useCallback((patient) => {
    setSelectedPatient(patient);
    setSearchTerm('');
    setFilteredPatients([]);
    setFile(null);
    setReportType('');
    setDescription('');
    loadPatientReports(patient.id);
  }, []);

  const loadPatientReports = (patientId) => {
    setReportsLoading(true);
    patientService.getLabReports(patientId)
      .then(res => setReports(res.data))
      .catch(() => setReports([]))
      .finally(() => setReportsLoading(false));
  };

  const handleUpload = async () => {
    if (!file) return setSnackbar({ open: true, message: 'Please select a file.', severity: 'error' });
    if (!reportType) return setSnackbar({ open: true, message: 'Please select a report type.', severity: 'error' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('reportType', reportType);
    if (description) formData.append('description', description);

    setUploading(true);
    try {
      await patientService.uploadLabReport(selectedPatient.id, formData);
      setSnackbar({ open: true, message: 'Report uploaded successfully.', severity: 'success' });
      setFile(null);
      setReportType('');
      setDescription('');
      loadPatientReports(selectedPatient.id);
      loadMyReports();
    } catch {
      setSnackbar({ open: true, message: 'Upload failed. Please try again.', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await labReportService.deleteReport(deleteConfirm.id);
      setSnackbar({ open: true, message: 'Report deleted.', severity: 'success' });
      setDeleteConfirm(null);
      if (selectedPatient) loadPatientReports(selectedPatient.id);
      loadMyReports();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete report.', severity: 'error' });
    }
  };

  const openFile = (report) => {
    setViewingReport(report);
    setReportViewerOpen(true);
  };

  const formatDate = (dt) => new Date(dt).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Lab Report Upload" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>

          {/* ── Left column: Search + Upload ── */}
          <Grid item xs={12} md={5}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Search Patient
              </Typography>

              <TextField
                fullWidth
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {patientsLoading ? <CircularProgress size={18} /> : <Search />}
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />

              {/* Dropdown results */}
              {filteredPatients.length > 0 && (
                <Paper variant="outlined" sx={{ mb: 2, maxHeight: 260, overflow: 'auto' }}>
                  {filteredPatients.map(p => (
                    <Box
                      key={p.id}
                      sx={{ px: 2, py: 1.2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => handleSelectPatient(p)}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {p.firstName} {p.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.phoneNumber} &nbsp;·&nbsp; {p.gender}, {new Date().getFullYear() - new Date(p.dateOfBirth).getFullYear()} yrs
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              )}

              {/* Selected patient */}
              {selectedPatient && (
                <>
                  <Box sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 1, p: 1.5, mb: 2 }}>
                    <Typography variant="body2" fontWeight={600} color="primary.dark">
                      Selected: {selectedPatient.firstName} {selectedPatient.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedPatient.phoneNumber} &nbsp;·&nbsp; {selectedPatient.gender}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Upload Report
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Report Type *"
                        value={reportType}
                        onChange={e => setReportType(e.target.value)}
                      >
                        {REPORT_TYPES.map(t => (
                          <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description (optional)"
                        placeholder="e.g., Fasting blood glucose, CBC with differential..."
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        startIcon={<CloudUpload />}
                        sx={{ py: 1.5 }}
                      >
                        {file ? file.name : 'Choose File (PDF / Image)'}
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.jpg,.jpeg,.png,.bmp,.tiff,.webp"
                          onChange={e => setFile(e.target.files[0] || null)}
                        />
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
                        onClick={handleUpload}
                        disabled={uploading || !file || !reportType}
                      >
                        {uploading ? 'Uploading...' : 'Upload Report'}
                      </Button>
                    </Grid>
                  </Grid>
                </>
              )}
            </Paper>
          </Grid>

          {/* ── Right column: Reports for selected patient ── */}
          <Grid item xs={12} md={7}>
            {selectedPatient ? (
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Reports — {selectedPatient.firstName} {selectedPatient.lastName}
                </Typography>

                {reportsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : reports.length === 0 ? (
                  <Alert severity="info">No reports uploaded for this patient yet.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Report Type</TableCell>
                          <TableCell>File</TableCell>
                          <TableCell>Lab</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reports.map(r => (
                          <TableRow key={r.id} hover>
                            <TableCell>
                              <Chip
                                label={r.reportType || 'Report'}
                                size="small"
                                icon={<Science fontSize="small" />}
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" noWrap sx={{ maxWidth: 160, display: 'block' }}>
                                {r.fileName}
                              </Typography>
                              {r.description && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160, display: 'block' }}>
                                  {r.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{r.labName || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{formatDate(r.uploadedAt)}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" color="primary" onClick={() => openFile(r)} title="View">
                                <Visibility fontSize="small" />
                              </IconButton>
                              {(isAdmin || r.uploadedByUserId === currentUser?.id) && (
                                <IconButton size="small" color="error" onClick={() => setDeleteConfirm(r)} title="Delete">
                                  <Delete fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            ) : (
              /* My recent uploads panel */
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  My Recent Uploads
                </Typography>
                {myReportsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : myReports.length === 0 ? (
                  <Alert severity="info">No reports uploaded yet. Search for a patient to get started.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Patient</TableCell>
                          <TableCell>Report Type</TableCell>
                          <TableCell>File</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {myReports.map(r => (
                          <TableRow key={r.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {r.patient ? `${r.patient.firstName} ${r.patient.lastName}` : `Patient #${r.patientId}`}
                              </Typography>
                              {r.patient && (
                                <Typography variant="caption" color="text.secondary">
                                  {r.patient.phoneNumber}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip label={r.reportType || 'Report'} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" noWrap sx={{ maxWidth: 140, display: 'block' }}>
                                {r.fileName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{formatDate(r.uploadedAt)}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" color="primary" onClick={() => openFile(r)} title="View">
                                <Visibility fontSize="small" />
                              </IconButton>
                              {(isAdmin || r.uploadedByUserId === currentUser?.id) && (
                                <IconButton size="small" color="error" onClick={() => setDeleteConfirm(r)} title="Delete">
                                  <Delete fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Inline Report Viewer */}
      <Dialog
        open={reportViewerOpen}
        onClose={() => setReportViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BiotechOutlined sx={{ color: '#00897b' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{viewingReport?.reportType || 'Lab Report'}</Typography>
              <Typography variant="caption" color="text.secondary">{viewingReport?.fileName}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<OpenInNew />}
              onClick={() => window.open(`${API_BASE}/${(viewingReport?.fileUrl || '').replace(/\\/g, '/')}`, '_blank')}
            >
              Open in new tab
            </Button>
            <Button size="small" onClick={() => setReportViewerOpen(false)}>Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {viewingReport && (() => {
            const url = `${API_BASE}/${viewingReport.fileUrl.replace(/\\/g, '/')}`;
            const isImage = /\.(jpg|jpeg|png|bmp|webp|tiff?)$/i.test(viewingReport.fileName);
            return isImage ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', p: 2 }}>
                <img src={url} alt={viewingReport.fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </Box>
            ) : (
              <iframe src={url} title={viewingReport.fileName} style={{ flex: 1, width: '100%', height: '100%', border: 'none' }} />
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Report</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteConfirm?.reportType || 'report'}</strong> — <em>{deleteConfirm?.fileName}</em>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default LabReportsPage;
